/**
 * Aba Dados bancários & PIX do funcionário (E10b — integração Convenia §3.3):
 * somente leitura — os dados chegam pelo sync (domínio "bancario"). Contas têm
 * vigência AAAAMM: a troca de conta não apaga o histórico.
 */
import { useEffect, useState } from "react";
import { Badge, Card, Group, Table, Text, Title } from "@mantine/core";
import { api } from "../../api";
import { fmtCompetencia } from "../../format";
import { Carregando, Vazio } from "../../componentes/Estados";
import { TabelaDados, Th } from "../../componentes/listas";

interface Conta {
  id: string;
  bancoCodigo: string | null;
  bancoNome: string | null;
  agencia: string | null;
  conta: string | null;
  digito: string | null;
  tipoConta: string | null;
  modalidade: string | null;
  pix: string | null;
  principal: boolean;
  vigenciaInicio: number;
  vigenciaFim: number | null;
}

export function BancarioTab({ funcionarioId }: { funcionarioId: string }) {
  const [contas, setContas] = useState<Conta[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    api
      .get<{ contas: Conta[] }>(`/funcionarios/${funcionarioId}/contas-bancarias`)
      .then((r) => setContas(r.contas))
      .catch(setErro);
  }, [funcionarioId]);

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!contas) return <Carregando linhas={4} />;

  return (
    <>
      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Contas bancárias & PIX <Badge variant="light">{contas.filter((c) => c.vigenciaFim === null).length}</Badge>
        </Title>
        {contas.length === 0 ? (
          <Vazio texto="Nenhuma conta sincronizada — rode o sync Convenia (domínio bancario) para esta pessoa." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Banco</Th>
                <Th>Agência</Th>
                <Th>Conta</Th>
                <Th>Tipo</Th>
                <Th>PIX</Th>
                <Th>Vigência</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {contas.map((c) => (
                <Table.Tr key={c.id} c={c.vigenciaFim !== null ? "dimmed" : undefined}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {c.bancoNome ?? "—"}
                      {c.bancoCodigo ? ` (${c.bancoCodigo})` : ""}
                    </Text>
                    {c.principal && c.vigenciaFim === null && (
                      <Badge size="xs" color="sillis" variant="light">
                        principal
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">{c.agencia ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {c.conta ? `${c.conta}${c.digito ? `-${c.digito}` : ""}` : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {[c.tipoConta, c.modalidade].filter(Boolean).join(" · ") || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="xs">{c.pix ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {fmtCompetencia(c.vigenciaInicio)}
                      {c.vigenciaFim ? ` → ${fmtCompetencia(c.vigenciaFim)}` : " → vigente"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>
      <Group>
        <Text size="xs" c="dimmed">
          Dados somente leitura — a fonte é o Convenia (sync na tela Integração Convenia). O pagamento dos líquidos
          (Omie) continua manual nesta fase.
        </Text>
      </Group>
    </>
  );
}
